import React, { ReactNode, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import CssBaseline from '@mui/material/CssBaseline';
import { OktaAuth } from '@okta/okta-auth-js';
import AlertsProvider from 'src/components/AlertsProvider';
import { AuthProvider } from 'src/auth';
import { createOktaAuth } from 'src/auth/auth';
import { OktaConfig } from 'src/auth/types';
import { oktaAuthStore } from 'src/auth/oktaAuthStore';
import { AppConfigProvider, AppConfig } from 'src/config';
import store from 'src/redux/store';
import theme from 'src/theme';
import { fetchClientConfig } from 'src/services/configService';
import FullScreenLoader from 'src/components/FullScreenLoader';
import ConfigErrorFallback from 'src/components/ConfigErrorFallback';
import { FeatureFlagsProvider } from 'src/contexts/FeatureFlagsContext';

type Props = {
  children: ReactNode;
};

function AppProviders({ children }: Props) {
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [configError, setConfigError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadConfig = async () => {
    setIsLoading(true);
    setConfigError(null);

    try {
      const config = await fetchClientConfig();

      const oktaConfig: OktaConfig = {
        issuer: config.oktaIssuer,
        clientId: config.reactClientId,
      };

      const appConfiguration: AppConfig = {
        oktaIssuer: config.oktaIssuer,
        reactClientId: config.reactClientId,
        appVersion: config.appVersion,
        menderSsoGuid: config.menderSsoGuid,
        msalClientId: config.msalClientId,
        msalTenantId: config.msalTenantId,
        pepsenseUrl: config.pepsenseUrl,
        clusterDetails: config.clusterDetails,
        clusterZone: config.clusterZone,
        edgePlatformStandardVersion: config.edgePlatformStandardVersion ?? {},
      };

      const auth = createOktaAuth(oktaConfig);
      oktaAuthStore.setInstance(auth);
      setOktaAuth(auth);
      setAppConfig(appConfiguration);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('[AppProviders] Failed to load configuration:', err);
      setConfigError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  if (isLoading) {
    return (
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <FullScreenLoader />
        </ThemeProvider>
      </Provider>
    );
  }

  if (configError || !oktaAuth || !appConfig) {
    return (
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ConfigErrorFallback
            error={
              configError || new Error('Failed to initialize authentication')
            }
            onRetry={loadConfig}
          />
        </ThemeProvider>
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <AppConfigProvider config={appConfig}>
            <AuthProvider oktaAuth={oktaAuth}>
              <CssBaseline />
              <AlertsProvider>
                <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
              </AlertsProvider>
            </AuthProvider>
          </AppConfigProvider>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
}

export default AppProviders;
